import { models, stores } from '@kurtosys/app-start';
import { EventTypes } from '@kurtosys/event-manager/dist/models/EventTypes.js';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { action, computed, makeObservable } from 'mobx';

import { LIBRARY_COMPONENTS_CONFIGURATION } from '../../../configuration/libraryComponentsConfiguration.js';
import { MockData } from '../../../configuration/mockData/index.js';
import { IComponentStyles } from '../../../models/app/IComponentStyles.js';
import { IConfiguration } from '../../../models/app/IConfiguration.js';
import { TStoreContext } from '../../../models/app/TStoreContext.js';
import { ISplitTableInputs } from '../../../models/table/ISplitTableInputs.js';
import { splitTable } from '../../../utils/splitTable.js';
import { CardStore } from '../../Card/stores/CardStore.js';
import { ColumnSelectorStore } from '../../ColumnSelector/stores/ColumnSelectorStore.js';
import { Feature } from '../../shared/Feature.js';
import { TableStore } from '../../Table/stores/TableStore.js';
import { IAppComponents } from '../models/IAppComponents.js';

/* [Component: appStoreComponentImport] */

let devConfig: models.IAppDevelopmentConfig = {
	applicationClientConfigurationIds: null,
	applicationClientConfigurations: null,
	authentication: null,
	configuration: null,
	styles: null,
	libraryComponentsConfiguration: LIBRARY_COMPONENTS_CONFIGURATION,
};

if (process.env.NODE_ENV !== 'production') {
	devConfig = {
		...devConfig,
		applicationClientConfigurationIds: async () => {
			const { APPLICATION_CLIENT_CONFIGURATION_IDS } = await import(
				'../../../configuration/development.applicationClientConfigurationIds.js'
			);
			return APPLICATION_CLIENT_CONFIGURATION_IDS;
		},
		applicationClientConfigurations: async () => {
			const { APPLICATION_CLIENT_CONFIGURATIONS } = await import(
				'../../../configuration/development.applicationClientConfigurations.js'
			);
			return APPLICATION_CLIENT_CONFIGURATIONS;
		},
		authentication: async () => {
			const { AUTHENTICATION } = await import('../../../configuration/development.authentication.js');
			return AUTHENTICATION;
		},
		configuration: async () => {
			const { CONFIGURATION } = await import('../../../configuration/development.config.js');
			return CONFIGURATION;
		},
		styles: async () => {
			const { STYLES } = await import('../../../configuration/development.styles.js');
			return STYLES;
		},
	};
}

export class AppStore extends stores.base.AppStoreBase<IConfiguration, IComponentStyles> {
	static EVENT_BUS_KEY: EventTypes = EventTypes.GLOBAL_INPUTS_CHANGE;
	url: URL;

	@computed
	get cardStore() {
		return this.storeContext.get<CardStore>('cardStore');
	}

	@computed
	get tableStore() {
		return this.storeContext.get<TableStore>('tableStore');
	}

	@computed
	get columnSelectorStore() {
		return this.storeContext.get<ColumnSelectorStore>('columnSelectorStore');
	}

	constructor(
		element: HTMLElement,
		url: string,
		storeContext: TStoreContext,
		manifest: IManifest,
		mockData?: MockData,
	) {
		super(element, url, storeContext, manifest, Feature, devConfig, mockData);
		this.url = new URL(url);
		if (!(window as any)['__ksysSplitAppFunctions__']) {
			(window as any)['__ksysSplitAppFunctions__'] = {};
		}
		if (manifest.packageName) {
			(window as any)['__ksysSplitAppFunctions__'][manifest.packageName] = splitTable;
		}
		makeObservable(this);
	}

	get hasData(): boolean {
		return this.tableStore.hasData === true;
	}

	@computed
	get paginationOptions(): models.IPaginationOptions {
		return {
			query: this.tableStore?.dynamicRows,
			overridingExecutionOptions: this.tableStore.dynamicRowsExecutionOptions,
		};
	}

	@action
	contextsDidUpdateAfter = async () => {
		await this.cardStore.initialize();
		await this.tableStore.initialize();
	};

	get components(): IAppComponents {
		return {
			/* [Component: appStoreComponent] */
		};
	}

	@computed
	get splitTableOptions() {
		const inputs = (this.appParamsHelper.inputs || {}) as ISplitTableInputs;
		const limit = parseInt(inputs.limit || '0', 10);
		const start = parseInt(inputs.start || '0', 10);
		return {
			limit,
			start,
			inputs,
			hideDisclaimer: inputs.hasDisclaimers === 'false',
			hideCardHeader: inputs.hasCardHeader === 'false',
			hideRows: start === -1 || limit === 0,
			mustLimitRows: start || limit,
		};
	}

	@action
	setIsInitialized = () => {
		this.isInitialized = true;
	};

	async customInitializeAfter() {
		// We initialize the card store first for the global disclaimer numbering to
		// start with cart title/subTitle if any
		if (this.cardStore) {
			await this.cardStore.initialize();
		}
		if (this.columnSelectorStore) {
			await this.columnSelectorStore.initialize();
		}
		if (this.tableStore) {
			await this.tableStore.initialize();
		}
		this.setIsInitialized();
	}
}
